import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  ProjectNoteFact,
  ProjectNoteDismissedConflictPair,
  ProjectNote,
  ProjectNoteInput,
  ProjectNoteKind,
  ProjectNotePatch,
  UseProjectNotesOptions,
  UseProjectNotesResult
} from "./types";

export const PROJECT_NOTE_KIND_OPTIONS: readonly ProjectNoteKind[] = [
  "clarification",
  "exclusion",
  "commissioning",
  "requirement-amendment"
];

export const PROJECT_NOTE_KIND_LABELS: Record<ProjectNoteKind, string> = {
  clarification: "Clarification",
  exclusion: "Exclusion",
  commissioning: "Commissioning",
  "requirement-amendment": "Requirement amendment"
};

const normaliseNoteText = (text: string): string => text.replace(/\s+/g, " ").trim();

const safeProjectId = (projectId: string): string =>
  projectId
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "project";

const defaultNow = (): string => new Date().toISOString();

const cloneNotes = (notes: readonly ProjectNote[] | undefined): ProjectNote[] =>
  notes
    ? notes.map((note) => ({
        ...note,
        ...(note.subjectRef ? { subjectRef: { ...note.subjectRef } } : {}),
        ...(note.aiProvenance ? { aiProvenance: { ...note.aiProvenance } } : {}),
        ...(note.conflictsWith ? { conflictsWith: [...note.conflictsWith] } : {}),
        ...(note.dismissedConflictPairs
          ? {
              dismissedConflictPairs: note.dismissedConflictPairs.map((pair) => ({
                noteIds: [pair.noteIds[0], pair.noteIds[1]],
                contentFingerprints: [
                  pair.contentFingerprints[0],
                  pair.contentFingerprints[1]
                ]
              }))
            }
          : {})
      }))
    : [];

export const projectNoteKindLabel = (kind: ProjectNoteKind): string =>
  PROJECT_NOTE_KIND_LABELS[kind];

export const buildProjectNoteFacts = (
  notes: readonly ProjectNote[]
): ProjectNoteFact[] =>
  notes.map((note) => {
    const label = projectNoteKindLabel(note.kind);
    const subject = note.subjectRef ? ` Linked to ${note.subjectRef.label}.` : "";
    const author =
      (note.authorType ?? "user") === "ai"
        ? " AI-authored note."
        : " User-authored note.";
    const status =
      note.status && note.status !== "active" ? ` Status: ${note.status}.` : "";

    return {
      id: `note-fact-${note.id}`,
      kind: "note",
      plainText: `${label} note captured ${note.createdAt}.${author}${status}${subject} ${note.text}`,
      refs: [
        {
          kind: "note",
          id: note.id,
          label
        }
      ],
      confidence: "medium"
    };
  });

export function useProjectNotes({
  projectId,
  initialNotes,
  now = defaultNow,
  idFactory
}: UseProjectNotesOptions): UseProjectNotesResult {
  const sequence = useRef(0);
  const [notes, setNotes] = useState<ProjectNote[]>(() => cloneNotes(initialNotes));

  useEffect(() => {
    sequence.current = 0;
    setNotes(cloneNotes(initialNotes));
  }, [projectId]);

  const nextId = useCallback((): string => {
    if (idFactory) {
      return idFactory();
    }

    sequence.current += 1;
    return `note-${safeProjectId(projectId)}-${sequence.current}`;
  }, [idFactory, projectId]);

  const addNote = useCallback(
    (input: ProjectNoteInput): ProjectNote => {
      const text = normaliseNoteText(input.text);

      if (!text) {
        throw new Error("Project note text is required.");
      }

      const authorType = input.authorType ?? "user";
      const createdAt = now();
      const conflictsWith =
        authorType === "ai"
          ? conflictingNoteIds({
              candidate: {
                kind: input.kind,
                text,
                ...(input.subjectRef ? { subjectRef: input.subjectRef } : {})
              },
              notes
            })
          : [];
      const note: ProjectNote = {
        id: nextId(),
        kind: input.kind,
        text,
        authorType,
        status:
          authorType === "ai" && conflictsWith.length > 0
            ? "conflict_flagged"
            : "active",
        createdAt,
        updatedAt: createdAt,
        ...(input.subjectRef ? { subjectRef: input.subjectRef } : {}),
        ...(input.aiProvenance ? { aiProvenance: input.aiProvenance } : {}),
        ...(conflictsWith.length > 0 || input.conflictsWith
          ? {
              conflictsWith: [
                ...new Set([...(input.conflictsWith ?? []), ...conflictsWith])
              ]
            }
          : {}),
        ...(input.promotedRequirementId
          ? { promotedRequirementId: input.promotedRequirementId }
          : {})
      };

      setNotes((current) => {
        // Surface the conflict from both sides: the note(s) this new note
        // contradicts gain a back-reference to it, not just the other way
        // around, so a user reading the original note sees the conflict too.
        const withBackReferences =
          conflictsWith.length > 0
            ? current.map((existing) =>
                conflictsWith.includes(existing.id)
                  ? addConflictBackReference(existing, note.id, createdAt)
                  : existing
              )
            : current;

        return [note, ...withBackReferences];
      });
      return note;
    },
    [nextId, notes, now]
  );

  const editNote = useCallback(
    (noteId: string, patch: ProjectNotePatch) => {
      setNotes((current) => {
        let matched = false;

        const updated = current.map((note) => {
          if (note.id !== noteId) {
            return note;
          }

          matched = true;

          const next: ProjectNote = {
            ...note,
            ...(patch.kind ? { kind: patch.kind } : {}),
            ...(patch.authorType ? { authorType: patch.authorType } : {}),
            ...(patch.status ? { status: patch.status } : {}),
            updatedAt: now()
          };

          if (patch.text !== undefined) {
            const text = normaliseNoteText(patch.text);

            if (text) {
              next.text = text;
            }
          }

          if ("subjectRef" in patch) {
            if (patch.subjectRef === null) {
              delete next.subjectRef;
            }

            if (patch.subjectRef) {
              next.subjectRef = patch.subjectRef;
            }
          }

          if ("aiProvenance" in patch) {
            if (patch.aiProvenance === null) {
              delete next.aiProvenance;
            }

            if (patch.aiProvenance) {
              next.aiProvenance = patch.aiProvenance;
            }
          }

          if ("promotedRequirementId" in patch) {
            if (patch.promotedRequirementId === null) {
              delete next.promotedRequirementId;
            }

            if (patch.promotedRequirementId) {
              next.promotedRequirementId = patch.promotedRequirementId;
            }
          }

          if ("lastEditedBy" in patch) {
            if (patch.lastEditedBy) {
              next.lastEditedBy = patch.lastEditedBy;
            } else {
              delete next.lastEditedBy;
            }
          }

          if ("dismissedConflictPairs" in patch && patch.dismissedConflictPairs) {
            next.dismissedConflictPairs = cloneDismissedConflictPairs(
              patch.dismissedConflictPairs
            );
          }

          return next;
        });

        if (!matched) {
          return current;
        }

        const manualConflictsByNoteId = Object.prototype.hasOwnProperty.call(
          patch,
          "conflictsWith"
        )
          ? new Map([[noteId, [...(patch.conflictsWith ?? [])]] as const])
          : undefined;

        return recomputeNoteConflicts(updated, now(), {
          ...(manualConflictsByNoteId ? { manualConflictsByNoteId } : {})
        });
      });
    },
    [now]
  );

  const deleteNote = useCallback(
    (noteId: string) => {
      setNotes((current) => {
        const timestamp = now();

        // Scrub the deleted note's id out of every other note's
        // conflict state — otherwise a surviving note can stay permanently
        // flagged or keep a stale dismissal for a note that no longer exists.
        return current
          .filter((note) => note.id !== noteId)
          .map((note) => scrubDeletedNoteReferences(note, noteId, timestamp));
      });
    },
    [now]
  );

  const resolveConflict = useCallback(
    (noteId: string) => {
      setNotes((current) => {
        const target = current.find((note) => note.id === noteId);
        const linkedIds = target?.conflictsWith ?? [];

        if (linkedIds.length === 0 && target?.status !== "conflict_flagged") {
          return current;
        }

        const timestamp = now();
        const dismissedPairs = linkedIds.flatMap((linkedId) => {
          const linked = current.find((note) => note.id === linkedId);

          return linked && target ? [dismissedConflictPairFor(target, linked)] : [];
        });

        const resolved = current.map((note) => {
          if (note.id === noteId) {
            const next: ProjectNote = { ...note, updatedAt: timestamp };

            delete next.conflictsWith;

            if (next.status === "conflict_flagged") {
              next.status = "active";
            }

            return dismissedPairs.reduce(addDismissedConflictPair, next);
          }

          if (linkedIds.includes(note.id)) {
            const withoutBackReference = removeConflictBackReference(
              note,
              noteId,
              timestamp
            );
            const pair = dismissedPairs.find((candidate) =>
              candidate.noteIds.includes(note.id)
            );

            return pair
              ? addDismissedConflictPair(withoutBackReference, pair)
              : withoutBackReference;
          }

          return note;
        });

        return recomputeNoteConflicts(resolved, timestamp);
      });
    },
    [now]
  );

  const resetNotes = useCallback((nextNotes?: readonly ProjectNote[]) => {
    sequence.current = 0;
    setNotes(cloneNotes(nextNotes));
  }, []);

  const upsertNote = useCallback((note: ProjectNote) => {
    const nextNote = cloneNotes([note])[0];

    if (!nextNote) {
      return;
    }

    setNotes((current) => {
      const existingIndex = current.findIndex((candidate) => candidate.id === note.id);

      const replaced =
        existingIndex === -1
          ? [nextNote, ...current]
          : current.map((candidate, index) =>
              index === existingIndex ? nextNote : candidate
            );

      const timestamp = nextNote.updatedAt ?? nextNote.createdAt;
      const manualConflictsByNoteId = nextNote.conflictsWith
        ? new Map([[nextNote.id, [...nextNote.conflictsWith]] as const])
        : undefined;

      return recomputeNoteConflicts(replaced, timestamp, {
        ...(manualConflictsByNoteId ? { manualConflictsByNoteId } : {})
      });
    });
  }, []);

  const noteFacts = useMemo(() => buildProjectNoteFacts(notes), [notes]);

  return {
    projectId,
    notes,
    noteFacts,
    addNote,
    editNote,
    deleteNote,
    resolveConflict,
    resetNotes,
    upsertNote
  };
}

/**
 * Adds `otherId` to a note's `conflictsWith` list (a back-reference from the
 * note it conflicts with), so the conflict renders on both cards rather than
 * only the note whose contradiction triggered detection. Flags the note as
 * `conflict_flagged` unless it has already been promoted.
 */
function addConflictBackReference(
  note: ProjectNote,
  otherId: string,
  timestamp: string
): ProjectNote {
  if (note.conflictsWith?.includes(otherId)) {
    return note;
  }

  const next: ProjectNote = {
    ...note,
    conflictsWith: [...(note.conflictsWith ?? []), otherId],
    updatedAt: timestamp
  };

  if (next.status !== "promoted_to_requirement") {
    next.status = "conflict_flagged";
  }

  return next;
}

/**
 * Removes `otherId` from a note's `conflictsWith` list. Used when the other
 * note is deleted, when a conflict is explicitly resolved, or when an edit
 * means the two notes no longer contradict each other. Clears the
 * `conflict_flagged` status once no conflicts remain.
 */
function removeConflictBackReference(
  note: ProjectNote,
  otherId: string,
  timestamp: string
): ProjectNote {
  if (!note.conflictsWith?.includes(otherId)) {
    return note;
  }

  const conflictsWith = note.conflictsWith.filter((id) => id !== otherId);
  const next: ProjectNote = { ...note, updatedAt: timestamp };

  if (conflictsWith.length > 0) {
    next.conflictsWith = conflictsWith;
  } else {
    delete next.conflictsWith;

    if (next.status === "conflict_flagged") {
      next.status = "active";
    }
  }

  return next;
}

function scrubDeletedNoteReferences(
  note: ProjectNote,
  deletedNoteId: string,
  timestamp: string
): ProjectNote {
  const withoutConflict = removeConflictBackReference(note, deletedNoteId, timestamp);
  const dismissedConflictPairs = withoutConflict.dismissedConflictPairs?.filter(
    (pair) => !pair.noteIds.includes(deletedNoteId)
  );

  if (
    dismissedConflictPairs?.length === withoutConflict.dismissedConflictPairs?.length
  ) {
    return withoutConflict;
  }

  const next: ProjectNote = { ...withoutConflict, updatedAt: timestamp };

  if (dismissedConflictPairs && dismissedConflictPairs.length > 0) {
    next.dismissedConflictPairs = dismissedConflictPairs;
  } else {
    delete next.dismissedConflictPairs;
  }

  return next;
}

function recomputeNoteConflicts(
  notes: readonly ProjectNote[],
  timestamp: string,
  options: {
    manualConflictsByNoteId?: ReadonlyMap<string, readonly string[]>;
  } = {}
): ProjectNote[] {
  const byId = new Map(notes.map((note) => [note.id, note]));
  const withFreshDismissals = notes.map((note) =>
    pruneInvalidDismissedConflictPairs(note, byId, timestamp)
  );
  const conflictsByNoteId = new Map<string, string[]>();
  const backReferencesByNoteId = new Map<string, string[]>();

  for (const note of withFreshDismissals) {
    const manualConflicts = options.manualConflictsByNoteId?.get(note.id) ?? [];
    const heuristicConflicts =
      (note.authorType ?? "user") === "ai"
        ? conflictingNoteIds({
            candidate: note,
            notes: withFreshDismissals,
            ignoreNoteId: note.id
          })
        : [];
    const ownConflicts = uniqueStrings([...manualConflicts, ...heuristicConflicts]);

    if (ownConflicts.length === 0) {
      continue;
    }

    conflictsByNoteId.set(note.id, ownConflicts);

    for (const otherId of ownConflicts) {
      backReferencesByNoteId.set(otherId, [
        ...(backReferencesByNoteId.get(otherId) ?? []),
        note.id
      ]);
    }
  }

  return withFreshDismissals.map((note) => {
    const conflictsWith = uniqueStrings([
      ...(conflictsByNoteId.get(note.id) ?? []),
      ...(backReferencesByNoteId.get(note.id) ?? [])
    ]);
    const next: ProjectNote = { ...note };

    if (conflictsWith.length > 0) {
      next.conflictsWith = conflictsWith;

      if (next.status !== "promoted_to_requirement") {
        next.status = "conflict_flagged";
      }
    } else {
      delete next.conflictsWith;

      if (next.status === "conflict_flagged") {
        next.status = "active";
      }
    }

    return next;
  });
}

function pruneInvalidDismissedConflictPairs(
  note: ProjectNote,
  notesById: ReadonlyMap<string, ProjectNote>,
  timestamp: string
): ProjectNote {
  if (!note.dismissedConflictPairs || note.dismissedConflictPairs.length === 0) {
    return note;
  }

  const dismissedConflictPairs = note.dismissedConflictPairs.filter((pair) => {
    const left = notesById.get(pair.noteIds[0]);
    const right = notesById.get(pair.noteIds[1]);

    return Boolean(left && right && dismissedConflictPairMatches(pair, left, right));
  });

  if (dismissedConflictPairs.length === note.dismissedConflictPairs.length) {
    return note;
  }

  const next: ProjectNote = { ...note, updatedAt: timestamp };

  if (dismissedConflictPairs.length > 0) {
    next.dismissedConflictPairs = dismissedConflictPairs;
  } else {
    delete next.dismissedConflictPairs;
  }

  return next;
}

function addDismissedConflictPair(
  note: ProjectNote,
  pair: ProjectNoteDismissedConflictPair
): ProjectNote {
  return {
    ...note,
    dismissedConflictPairs: dedupeDismissedConflictPairs([
      ...(note.dismissedConflictPairs ?? []),
      pair
    ])
  };
}

function dedupeDismissedConflictPairs(
  pairs: readonly ProjectNoteDismissedConflictPair[]
): ProjectNoteDismissedConflictPair[] {
  const byPairKey = new Map<string, ProjectNoteDismissedConflictPair>();

  for (const pair of pairs) {
    const normalized = normalizeDismissedConflictPair(pair);

    if (normalized) {
      byPairKey.set(pairKey(normalized.noteIds), normalized);
    }
  }

  return [...byPairKey.values()];
}

function cloneDismissedConflictPairs(
  pairs: readonly ProjectNoteDismissedConflictPair[]
): ProjectNoteDismissedConflictPair[] {
  return dedupeDismissedConflictPairs(pairs);
}

function normalizeDismissedConflictPair(
  pair: ProjectNoteDismissedConflictPair
): ProjectNoteDismissedConflictPair | undefined {
  const entries = pair.noteIds.map((id, index) => ({
    id,
    contentFingerprint: pair.contentFingerprints[index]
  }));

  if (
    entries.length !== 2 ||
    entries.some(
      (entry) =>
        typeof entry.id !== "string" ||
        entry.id.trim().length === 0 ||
        typeof entry.contentFingerprint !== "string" ||
        entry.contentFingerprint.trim().length === 0
    ) ||
    entries[0]?.id === entries[1]?.id
  ) {
    return undefined;
  }

  entries.sort((left, right) => left.id.localeCompare(right.id));

  const [first, second] = entries;

  if (
    !first ||
    !second ||
    typeof first.contentFingerprint !== "string" ||
    typeof second.contentFingerprint !== "string"
  ) {
    return undefined;
  }

  return {
    noteIds: [first.id, second.id],
    contentFingerprints: [first.contentFingerprint, second.contentFingerprint]
  };
}

function dismissedConflictPairFor(
  left: ProjectNote,
  right: ProjectNote
): ProjectNoteDismissedConflictPair {
  const noteIds = sortedNotePair(left.id, right.id);

  return {
    noteIds,
    contentFingerprints: noteIds.map((id) =>
      conflictContentFingerprint(id === left.id ? left : right)
    ) as [string, string]
  };
}

function dismissedConflictPairMatches(
  pair: ProjectNoteDismissedConflictPair,
  left: Pick<ProjectNote, "id" | "kind" | "text" | "subjectRef">,
  right: Pick<ProjectNote, "id" | "kind" | "text" | "subjectRef">
): boolean {
  const noteIds = sortedNotePair(left.id, right.id);

  if (pair.noteIds[0] !== noteIds[0] || pair.noteIds[1] !== noteIds[1]) {
    return false;
  }

  return pair.contentFingerprints.every((fingerprint, index) => {
    const note = pair.noteIds[index] === left.id ? left : right;

    return fingerprint === conflictContentFingerprint(note);
  });
}

function isDismissedConflictPair(
  left: Pick<
    ProjectNote,
    "id" | "kind" | "text" | "subjectRef" | "dismissedConflictPairs"
  >,
  right: ProjectNote
): boolean {
  const pairs = [
    ...(left.dismissedConflictPairs ?? []),
    ...(right.dismissedConflictPairs ?? [])
  ];

  return pairs.some((pair) => dismissedConflictPairMatches(pair, left, right));
}

function sortedNotePair(leftId: string, rightId: string): [string, string] {
  return leftId.localeCompare(rightId) <= 0 ? [leftId, rightId] : [rightId, leftId];
}

function pairKey(noteIds: readonly [string, string]): string {
  return `${noteIds[0]}\u0000${noteIds[1]}`;
}

function conflictContentFingerprint(
  note: Pick<ProjectNote, "kind" | "text" | "subjectRef">
): string {
  return JSON.stringify({
    version: 1,
    kind: note.kind,
    subjectRefId: note.subjectRef?.id ?? null,
    text: normaliseNoteText(note.text)
  });
}

function conflictingNoteIds(input: {
  candidate: Pick<ProjectNote, "kind" | "text" | "subjectRef"> &
    Partial<Pick<ProjectNote, "id" | "dismissedConflictPairs">>;
  notes: readonly ProjectNote[];
  ignoreNoteId?: string;
}): string[] {
  return input.notes.flatMap((note) => {
    if (note.id === input.ignoreNoteId) {
      return [];
    }

    const sameSubject =
      input.candidate.subjectRef?.id &&
      note.subjectRef?.id === input.candidate.subjectRef.id;
    const sameKind = note.kind === input.candidate.kind;

    if (!(sameSubject || sameKind)) {
      return [];
    }

    if (
      input.candidate.id &&
      isDismissedConflictPair(
        {
          id: input.candidate.id,
          kind: input.candidate.kind,
          text: input.candidate.text,
          ...(input.candidate.subjectRef
            ? { subjectRef: input.candidate.subjectRef }
            : {}),
          ...(input.candidate.dismissedConflictPairs
            ? { dismissedConflictPairs: input.candidate.dismissedConflictPairs }
            : {})
        },
        note
      )
    ) {
      return [];
    }

    return noteTextsContradict(input.candidate.text, note.text) ? [note.id] : [];
  });
}

function noteTextsContradict(left: string, right: string): boolean {
  const a = left.toLowerCase();
  const b = right.toLowerCase();
  const negative = /\b(no|not|exclude|excluded|without|omit|remove)\b/;
  const positive = /\b(include|included|required|provide|allow|must|shall|yes)\b/;
  const priceImpact = /\b(price impact|priced|allowance|allow for|included in price)\b/;
  const noPriceImpact = /\bno price impact|not priced|commissioning only\b/;

  return (
    (negative.test(a) && positive.test(b)) ||
    (positive.test(a) && negative.test(b)) ||
    (noPriceImpact.test(a) && priceImpact.test(b)) ||
    (priceImpact.test(a) && noPriceImpact.test(b))
  );
}

function uniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => value.trim().length > 0))];
}
